import React, { useState, useRef, useEffect } from "react";
import { Grid, GridCellProps, GridColumn, GridNoRecords, GridPageChangeEvent, GridSortChangeEvent } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { orderBy, process, SortDescriptor } from "@progress/kendo-data-query";
import { Popup } from "@progress/kendo-react-popup";
import {
  AutoQARule,
  UserAllocatedAutoQARule,
} from "../../../types/automatedQA/AutomatedQA";
import autoqarulesService from "../../../services/autoqarules.service";
import { Loader } from "@progress/kendo-react-indicators";
import useSwal from "../../../hooks/useSwal";
import { SweetAlertOptions } from "sweetalert2";
import CustomUserGridInfo from "../../../components/custom/grid/CustomUserGridInfo";
import { PageDataProps } from "./AutomatedQA";
import useTranslation from "../../../hooks/useTranslation";
import useLocale from "../../../hooks/useLocale";
import useMasterData from "../../../hooks/useMasterData";
import { User } from "../../../types/user";
import { Tooltip } from "@progress/kendo-react-tooltip";

interface ViewByUsersProps {
  userAllocatedAutoQARules: UserAllocatedAutoQARule[];
  setUpdateUserAllocatedAutoQARulesData:React.Dispatch<React.SetStateAction<UserAllocatedAutoQARule | undefined>>;
  setView: React.Dispatch<React.SetStateAction<"autoqa-list" | "assign-scorecard">>;
  view:"autoqa-list" | "assign-scorecard";
  parentLoader:boolean;
  error:string;
  fetchAllAutoQARules:() => Promise<void>;
  fetchAllUserAllocatedAutoQARules:(appendLastData?: UserAllocatedAutoQARule) => Promise<void>;
  page:PageDataProps;
  handlePageChange: (event: GridPageChangeEvent) => void;
  setPage: React.Dispatch<React.SetStateAction<PageDataProps>>;
  setUserAllocatedQARules:React.Dispatch<React.SetStateAction<UserAllocatedAutoQARule[]>>;
}

const ViewByUsers: React.FC<ViewByUsersProps> = ({
  userAllocatedAutoQARules,
  setUpdateUserAllocatedAutoQARulesData,
  setView,
  parentLoader,
  error,
  fetchAllAutoQARules,
  fetchAllUserAllocatedAutoQARules,
  view,
  page,
  handlePageChange,
  setPage,
  setUserAllocatedQARules
}) => {
  const masterData = useMasterData();
  const swal = useSwal();
  const trans=useTranslation("ViewByUsers");
  const localeCtx = useLocale();
  const [sort, setSort] = React.useState<SortDescriptor[]>([]);
  const [loader,setLoader]=useState<boolean>(false);

  useEffect(() => {
    if (
      !localeCtx?.selectedLocale?.current.componentTranslations[
        "ViewByUsers"
      ]
    ) {
      trans.fetchTranslations("ViewByUsers");
    }
  }, [localeCtx?.selectedLocale]);

  const CustomCellActions = (props: any) => {
    const deleteUserAllocatedAutomatedQARule=async()=>{
      setLoader(true);
      try{
        const swalOptions: SweetAlertOptions = {
          title: trans.fetchLabelKeyTranslation("DeleteSwalTitleOne","Are your sure?"),
          text: trans.fetchLabelKeyTranslation('DeleteSwalText',`The selected scorecards will be disabled for this user.`),
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: trans.fetchLabelKeyTranslation('DeleteSwalConfirm',"Confirm")
          ,
          cancelButtonText: trans.fetchLabelKeyTranslation('DeleteSwalCancel',`Cancel`),
        };
        const response = await swal.fire(swalOptions);
        if(response.isConfirmed){
          await autoqarulesService.assignDefaultScorecardAndAutomatedQARules([
            {id:props.dataItem.user.id,defaultScorecardId:0,autoQARuleIds:[]}
          ]);
          fetchAllAutoQARules();
          fetchAllUserAllocatedAutoQARules();
          const removedItemUserAllocatedQARulesList = userAllocatedAutoQARules.filter((item:UserAllocatedAutoQARule)=>item.user.id!==props.dataItem.user.id);
          const gridDataResult = process(
            removedItemUserAllocatedQARulesList,
            page
          );
          if (gridDataResult.data.length === 0 && gridDataResult.total > 0) {
            const currentPageNumber = Math.floor(
              gridDataResult.total / page.take
            );
            setPage({
              skip:
                gridDataResult.total % page.take === 0
                  ? (currentPageNumber - 1) * page.take
                  : currentPageNumber * page.take,
              take: page.take,
            });
          }
        }
      }catch(err){
        const swalOptions: SweetAlertOptions = {
          icon: "error",
          title: "Error",
          text: trans.fetchLabelKeyTranslation('DeleteSwalErrorTitle', `Cannot unassign Default scorecard and Outcome-to-Scorecard mapping from this user`),
          showCancelButton: false,
          confirmButtonColor: "#3085d6",
          confirmButtonText: trans.fetchLabelKeyTranslation('DeleteSwalErrorBtn',"Okay")
          ,
        };
        swal.fire(swalOptions);
      }finally{
        setLoader(false);
      }
    }

    return (
      <td className="text-center">
        <div className="iBtn d-flex justify-content">
          {masterData?.standardUsers.find((u:User)=>u.id == props.dataItem.user.id)?.licensePackage?.baseLicense.id===3 ? (
             <Tooltip
              anchorElement={"target"}
              position={"top"}
              parentTitle={true}
            >
              <span className="fs-18 m-r-15" title={trans.fetchLabelKeyTranslation("EditTitle","Edit")} >
                <Button onClick={()=>{
                  setUpdateUserAllocatedAutoQARulesData(props.dataItem);
                  setView("assign-scorecard");
                }} className="radius-50 iBtn-bg d-flex align-items-center justify-conter m-r-3 iconBtn">
                  <i className="bi bi-pencil fs-17 line-height-1"></i>
                </Button>
              </span>
              </Tooltip>
          ) : (
            <Tooltip
              anchorElement={"target"}
              position={"left"}
              parentTitle={true}
            >
              <span className="fs-18 m-r-15" title={trans.fetchLabelKeyTranslation("AssignUltimateLicense","Assign an ultimate license to this user to use auto QA.")}>
                <Button className="radius-50 iBtn-bg d-flex align-items-center justify-conter m-r-3 iconBtn">
                  <i className="bi bi-exclamation-triangle-fill text-danger"></i>
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip
            anchorElement={"target"}
            position={"top"}
            parentTitle={true}
          >
            <span className="fs-18 m-r-15" title={trans.fetchLabelKeyTranslation("DeleteTitle","Delete")}>
              <Button disabled={loader} onClick={deleteUserAllocatedAutomatedQARule} className="radius-50 iBtn-bg d-flex align-items-center justify-conter m-r-3 iconBtn">
                <i className="bi bi-trash3 fs-17 line-height-1"></i>
              </Button>
            </span>
          </Tooltip>
        </div>
      </td>
    );
  };

  const CustomCellAutoQaRules = (props: any) => {
    const { dataItem } = props;
    const [popupOpen, setPopupOpen] = useState(false);
    const anchorRef = useRef<HTMLSpanElement | null>(null);

    useEffect(() => {
      if (popupOpen) {
        const body = document.getElementById("root");
        const eventHandler = () => {
          setPopupOpen(false);
        };
        if (body != null) {
          body.addEventListener("click", eventHandler);
        }
        return () => body?.removeEventListener("click", eventHandler);
      }
    }, [popupOpen]);
  
    const togglePopup = () => {
      setPopupOpen((prev) => !prev);
    };
  
    return (
      <td>
        <div className="criteria">
        {dataItem.autoQARules
          .slice(0, 2)
          .map((rule: AutoQARule, index: number) => (
            <span
              key={index}
              className="badge bg-white border fs-13 mr-1 my-1 font-weight-normal"
            >
              {rule.callResult.text} -{" "}
              {rule.callResult && (
                rule.reasonCodes.length === 0 ?
                trans.fetchLabelKeyTranslation("AllReasonCodes","All Reason Codes") : rule.reasonCodes.map((code: any) => code.name).join(", ")
              )}{" "}
              {rule.callResult ? "-":""} {rule.scoreCard.scorecardName}
            </span>
          ))}
        {dataItem.autoQARules.length > 2 && (
          <span
            ref={anchorRef}
            className="text-primary cursor-pointer ml-2"
            onClick={togglePopup}
          >
            {popupOpen ? "less" : "more"}
          </span>
        )}
        <Popup
          anchor={anchorRef.current || document.body}
          show={popupOpen}
          popupClass="p-2 border bg-white shadow"
          anchorAlign={{ horizontal: "left", vertical: "bottom" }}
          popupAlign={{ horizontal: "left", vertical: "top" }}
        >
          <div>
            {dataItem.autoQARules
              .slice(2)
              .map((rule: AutoQARule, index: number) => (
                <div
                  key={index}
                  className="badge bg-white border fs-14 mr-1 font-weight-normal"
                >
                  {rule.callResult.text} -{" "}
                  {rule.callResult && (
                    rule.reasonCodes.length === 0 ?
                      trans.fetchLabelKeyTranslation("AllReasonCodes","All Reason Codes") : rule.reasonCodes.map((code: any) => code.name).join(", ")
                  )}{" "}
                  {rule.callResult ? "-":""} {rule.scoreCard.scorecardName}
                </div>
              ))}
          </div>
          </Popup>
          </div>
      </td>
    );
  };

  const CustomGCellWithCallParty = (callPartyprops: GridCellProps) => {
    const user = callPartyprops.dataItem.user;
    return (
      <td className="uCardCol">
        <div className="moreData">
          <div
            className="cursor-pointer"
          >
          <CustomUserGridInfo 
            userInfo={{
              id: user.id,
              loginUserId: user.loginUserId,
              firstName: user.firstName ? user.firstName : "",
              lastName: user.lastName ? user.lastName : "",
              image: user.image ? user.image : "",
              email: user.email,
              phoneNo: "",
              isArchived: user.isArchived,
            }}
            showWarningIcon={false}
          />
          </div>
        </div>
      </td>
    );
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    let tempSort = event.sort;
    if (
      event.sort.length === 0 &&
      sort.length !== 0
    ) {
      tempSort = sort.map((s) => {
        return { field: s.field, dir: "asc" };
      });
    }
    setUserAllocatedQARules(
      orderBy(userAllocatedAutoQARules ?? [], tempSort)
    );
    setSort(tempSort);
  }
  

  return (
    <div className="tableList float-left w-100">
      <div className="table-container table-responsive table-overflow-hidden borderLeftSecondChild table-mobile">
        <Grid
          data={orderBy(
            parentLoader ? [] : userAllocatedAutoQARules.slice(page.skip, page.take + page.skip),
            sort
          )}
          sortable={true}
          sort={sort}
          pageable={true}
          skip={page.skip}
          take={page.take}
          total={userAllocatedAutoQARules.length}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
        >
          <GridNoRecords>
            {parentLoader && <Loader type={"infinite-spinner"} />}
            {!parentLoader && error && (
              <span className="fs-15">
                <i className="bi bi-exclamation-triangle-fill tx-amber"></i>{" "}
                {trans.fetchLabelKeyTranslation("SomethingWentWrong","Uh Oh! Something Went Wrong. Please Try Again!")} 
              </span>
            )}
          </GridNoRecords>
          <GridColumn
            field="user.email"
            title={trans.fetchLabelKeyTranslation("Users","Users")}
            cell={CustomGCellWithCallParty}
            sortable={true}
            width={250}
          />
          <GridColumn
            field="defaultScoreCard.scorecardName"
             title={trans.fetchLabelKeyTranslation("GridDefaultSCTitle","Default Scorecard")}
            sortable={true}
            width={250}
          />
          <GridColumn
            field="autoQARules"
            title={trans.fetchLabelKeyTranslation("GridAdditionalCriteriaTitle","Additional Scorecards")}
            cell={CustomCellAutoQaRules}
            sortable={false}
          />
          <GridColumn
            cell={CustomCellActions}
            headerClassName="text-center"
            width={100}
            sortable={false}
          />
        </Grid>
      </div>
    </div>
  );
};

export default ViewByUsers;
